import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

/**
 * Self-change. The currently signed-in user supplies their current
 * password and a new one. The backend verifies, writes the new hash,
 * clears the {@code password_reset_required} flag (if set), and revokes
 * all live sessions — so the caller must re-login afterwards.
 */
export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: async (body: { currentPassword: string; newPassword: string }) => {
      await api.post('/v1/me/password', body)
    },
  })
}

/**
 * Admin force-reset. Admin sets a temporary password; the target user's
 * next login carries a {@code passwordResetRequired} flag and is gated to
 * the change-password surface until they self-change.
 */
export function useAdminResetPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string; tempPassword: string }) => {
      await api.post(`/v1/users/${params.userId}/password-reset`, {
        tempPassword: params.tempPassword,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}
